"use client";

import { useState } from "react";
import Image from "next/image";
import { ChevronDown } from "lucide-react";

interface ScreenshotTab {
	id: string;
	label: string;
	/** Descriptive alt for SEO and accessibility; falls back to `label` */
	alt?: string;
	src: string;
	available: boolean;
}

const URL_MAP: Record<string, string> = {
	dashboard: "eurolabs-peptides.com/affiliate/dashboard",
	links: "eurolabs-peptides.com/affiliate/links",
	conversions: "eurolabs-peptides.com/affiliate/conversions",
};

export function DashboardPreview({ tabs }: { tabs: ScreenshotTab[] }) {
	const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? "");
	const [mobileOpen, setMobileOpen] = useState(false);

	return (
		<>
			{/* Desktop: Tabs inside browser chrome */}
			<div className="hidden overflow-hidden rounded-2xl border border-border shadow-lg md:block">
				{/* Browser chrome */}
				<div className="flex items-center gap-2 border-b border-border bg-zinc-100 px-4 py-3">
					<span className="h-3 w-3 rounded-full bg-red-400" />
					<span className="h-3 w-3 rounded-full bg-yellow-400" />
					<span className="h-3 w-3 rounded-full bg-green-400" />
					<span className="ml-2 flex-1 rounded bg-white px-3 py-1 text-xs text-muted-foreground">
						{URL_MAP[activeTab] ?? "eurolabs-peptides.com/affiliate/dashboard"}
					</span>
				</div>

				{/* Tab bar */}
				<div className="flex gap-1 border-b border-border bg-zinc-50 p-1">
					{tabs.map((tab) => (
						<button
							key={tab.id}
							type="button"
							onClick={() => setActiveTab(tab.id)}
							className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
								activeTab === tab.id
									? "bg-white text-foreground shadow-sm"
									: "text-muted-foreground hover:text-foreground"
							}`}
						>
							{tab.label}
						</button>
					))}
				</div>

				{/* Tab content */}
				{tabs.map((tab) => (
					<div key={tab.id} className={activeTab === tab.id ? "block" : "hidden"}>
						{tab.available ? (
							<Image src={tab.src} alt={tab.alt ?? tab.label} width={1280} height={720} className="w-full" />
						) : (
							<div className="flex min-h-[320px] items-center justify-center bg-zinc-50">
								<p className="text-sm text-muted-foreground">{tab.label} screenshot (coming soon)</p>
							</div>
						)}
					</div>
				))}
			</div>

			{/* Mobile: Collapsible section */}
			<div className="md:hidden">
				<button
					type="button"
					onClick={() => setMobileOpen(!mobileOpen)}
					className="flex w-full items-center justify-between rounded-xl border border-border bg-card px-4 py-4 text-left shadow-sm"
				>
					<div className="flex items-center gap-3">
						<div className="flex gap-1">
							<span className="h-2.5 w-2.5 rounded-full bg-red-400" />
							<span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
							<span className="h-2.5 w-2.5 rounded-full bg-green-400" />
						</div>
						<span className="text-sm font-medium text-foreground">Dashboard ansehen</span>
					</div>
					<ChevronDown
						className={`h-5 w-5 text-muted-foreground transition-transform ${mobileOpen ? "rotate-180" : ""}`}
					/>
				</button>

				{mobileOpen && (
					<div className="mt-3 space-y-3">
						{tabs.map((tab) => (
							<div key={tab.id} className="overflow-hidden rounded-xl border border-border shadow-sm">
								<div className="flex items-center gap-2 border-b border-border bg-zinc-50 px-3 py-2">
									<span className="text-xs font-medium text-foreground">{tab.label}</span>
									<span className="flex-1 text-right text-[10px] text-muted-foreground">
										{URL_MAP[tab.id]}
									</span>
								</div>
								{tab.available ? (
									<Image
										src={tab.src}
										alt={tab.alt ?? tab.label}
										width={1280}
										height={720}
										className="w-full"
									/>
								) : (
									<div className="flex min-h-[120px] items-center justify-center bg-zinc-50">
										<p className="text-xs text-muted-foreground">{tab.label} (coming soon)</p>
									</div>
								)}
							</div>
						))}
					</div>
				)}
			</div>
		</>
	);
}
